import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';

interface Product {
  id: string;
  title: string;
  description: string[];
  image: string;
  photos: { label: string; url: string }[];
  videos: { label: string; url: string }[];
}

function VideoModal({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-4xl aspect-video bg-black rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={url}
          title="Video"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-colors"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}

export default function Products() {
  const { t } = useTranslation();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const products: Product[] = [
    {
      id: 'dsm',
      title: 'Anspitzwalzmaschinen - Typ DSM',
      description: [
        'Anspitzwalzmaschinen des Typs DSM werden zur Herstellung von Ziehangeln an Drähten, Rohren, Vierkant-, Sechskant-, Rund- und Flachmaterial aus Stahl und NE-Metall verwendet.',
        'Sämtliche Maschinen können mit und ohne Schere geliefert werden. Die Typen (+ v) werden zusätzlich mit einem vertikalen Walzenpaar ausgestattet, wenn ein Drehen des Drahtes nicht möglich ist.',
        'Die robuste Bauweise erlaubt eine hohe Beanspruchung. Der Keilriemen-Schwungrad-Antrieb gewährt einen ruhigen und schwingungsfreien Lauf. Die große Härte der Kaliber garantiert eine lange Lebensdauer. Individuelle Kalibrierungen sind möglich. Als Sonderausstattung kann jede Maschine mit einem Richtwerk ausgerüstet werden, mit dem das Drahtende 1-2 Meter gerichtet werden kann, um so das Anspitzen zu erleichtern. Das Richtwerk ist besonders für dicke Stahldrähte zu empfehlen.',
      ],
      image: 'https://www.bock-mf.de/typo3temp/pics/fb1872f1d2.jpg',
      photos: [
        { label: 'Foto', url: 'https://www.bock-mf.de/fileadmin/anwendungen/DSM-5_v.jpg' },
      ],
      videos: [],
    },
    {
      id: 'hsm',
      title: 'Hydraulische Schwenk-Anspitzwalzmaschinen - Typ HSM',
      description: [
        'Maschinen dieses Typs sind Konstruktionen, die auf engstem Raum den Drahtanspitzvorgang ermöglichen. Der Walzenkörper mit den hydraulisch angetriebenen Walzen wird - ebenfalls hydraulisch - in einem geschlossenen Gehäuse um 90° zwischen horizontaler und vertikaler Lage geschwenkt.',
        'Die Maschinen werden hauptsächlich eingesetzt, um die Querbewegungen des Bedienungsmannes zwischen horizontalen und vertikalen Walzen zu vermeiden.',
      ],
      image: 'https://www.bock-mf.de/typo3temp/pics/7d3e6f68e9.jpg',
      photos: [
        { label: 'Foto', url: 'https://www.bock-mf.de/fileadmin/anwendungen/HSM-7.jpg' },
      ],
      videos: [
        { label: 'Video 1', url: 'https://www.youtube.com/embed/JeA1BY8m4kU' },
        { label: 'Video 2', url: 'https://www.youtube.com/embed/36oXAsWNyTk' },
      ],
    },
    {
      id: 'asm',
      title: 'Anspitzschälmaschinen - Typ ASM',
      description: [
        'Anspitzschälmaschinen werden zur Herstellung von Ziehangeln an Drähten und Stangen aus Stahl und NE-Metallen mit Rund-, Vierkant- und Sechskantprofilen verwendet. Die Durchmesserreduzierung erfolgt durch eine spanabnehmende Bearbeitung.',
        'Der Schälkopf ist mit vier Schälwerkzeugen ausgestattet, die durch eine automatische Zentralverstellung vom maximalen bis zum minimalen Durchmesser zentrisch zugestellt werden. Der Vorschub und die Drehzahl des Schälkopfes lassen sich stufenlos regeln.',
      ],
      image: 'https://www.bock-mf.de/typo3temp/pics/255029f15c.jpg',
      photos: [
        { label: 'Foto 1', url: 'https://www.bock-mf.de/fileadmin/anwendungen/ASM-1.jpg' },
        { label: 'Foto 2', url: 'https://www.bock-mf.de/fileadmin/anwendungen/ASM-7_5.jpg' },
      ],
      videos: [
        { label: 'Video 1', url: 'https://www.youtube.com/embed/N6vZu2NF8lM' },
        { label: 'Video 2', url: 'https://www.youtube.com/embed/bxFAEnXbnP8' },
        { label: 'Video 3', url: 'https://www.youtube.com/embed/6xowBInNi6M' },
      ],
    },
    {
      id: 'hsp',
      title: 'Handanspitzwalzapparate - Typ HSP',
      description: [
        'Handanspitzwalzapparate der Baureihe HSP werden eingesetzt, um Ziehangeln an Drähten kleiner Durchmesser zu erzeugen.',
        'Der Anspitzwalzapparat ist mit Walzen ausgestattet, die in Wälzlagern gelagert sind, um die erforderliche Kraft zu reduzieren und die Flossenbildung an dem angespitzten Draht zu vermeiden.',
      ],
      image: 'https://www.bock-mf.de/typo3temp/pics/a3b527d086.jpg',
      photos: [
        { label: 'Foto', url: 'https://www.bock-mf.de/fileadmin/anwendungen/HSP.jpg' },
      ],
      videos: [],
    },
    {
      id: 'ezm',
      title: 'Kombinierte Anspitzwalz- und Einziehmaschinen - Typ EZM',
      description: [
        'Kombinierte Anspitzwalz- und Einziehmaschinen des Typs EZM verfügen über eine Anspitzwalzeinheit und eine Ziehscheibe mit Einziehzange, um einen Draht in mehrere Ziehsteine einzuziehen. Die Ziehsteine werden in einem robusten Ziehsteinhalter vor der Ziehscheibe gehalten und geführt.',
        'Die Maschinen werden hauptsächlich verwendet, um Nassziehprozesse vorzubereiten.',
      ],
      image: 'https://www.bock-mf.de/typo3temp/pics/d1d1c4aecf.jpg',
      photos: [
        { label: 'Foto 1', url: 'https://www.bock-mf.de/fileadmin/anwendungen/EZM.jpg' },
        { label: 'Foto 2', url: 'https://www.bock-mf.de/fileadmin/anwendungen/EZM-2_4.jpg' },
      ],
      videos: [],
    },
  ];

  return (
    <Layout>
      {/* Page Header */}
      <div className="bg-foreground-900 pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <span className="text-accent-400 text-sm font-medium uppercase tracking-wider mb-4 block">
            {t('nav.products')}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            {t('productsPage.title')}
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-2xl">
            {t('productsPage.subtitle')}
          </p>
        </div>
      </div>

      {/* Products */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-16 md:space-y-24">
          {products.map((product, index) => (
            <div
              key={product.id}
              className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden"
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Image */}
                <div className={`bg-background-100 p-8 md:p-12 flex items-center justify-center ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative w-full max-w-sm">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-auto object-contain rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`p-8 md:p-12 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    {product.title}
                  </h2>
                  <div className="space-y-4 mb-8">
                    {product.description.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-foreground-600 text-base leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Anwendungsbeispiele */}
                  <div className="space-y-4">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900">
                      Anwendungsbeispiele
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {product.photos.map((photo) => (
                        <a
                          key={photo.label}
                          href={photo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          <i className="ri-image-line"></i>
                          {photo.label}
                        </a>
                      ))}
                      {product.videos.map((video) => (
                        <button
                          key={video.label}
                          onClick={() => setActiveVideo(video.url)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-50 hover:bg-accent-100 text-accent-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          <i className="ri-play-circle-line"></i>
                          {video.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {activeVideo && (
        <VideoModal url={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </Layout>
  );
}